import { Building2, ChevronDown, ChevronRight, Loader2, Pencil, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import useLocale from '../contexts/LocaleContext';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useCreateFacilityType, useUpdateFacilityType, useDeleteFacilityType, useFacilityTypes, useCreateSubcategory, useUpdateSubcategory, useDeleteSubcategory } from '../hooks/useFacilities';
import type { CreateFacilityTypeDto, FacilitySubcategoryDto, FacilityTypeDto } from '../types/api';
import { getCurrentUser } from '../hooks/useAuth';
import { hasPermission } from '../lib/permissions';

const getLocalizedFacilityTypeName = (facilityType: FacilityTypeDto, locale: string): string => {
  switch (locale) {
    case 'fr': return facilityType.nameFrench || facilityType.name;
    case 'rw': return facilityType.nameKinyarwanda || facilityType.name;
    default: return facilityType.name;
  }
};

const getLocalizedSubcategoryName = (sub: FacilitySubcategoryDto, locale: string): string => {
  switch (locale) {
    case 'fr': return sub.nameFrench || sub.name;
    case 'rw': return sub.nameKinyarwanda || sub.name;
    default: return sub.name;
  }
};

function SubcategoryManager({
  facilityType,
  canCreate,
  canUpdate,
  canDelete,
}: {
  facilityType: FacilityTypeDto;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}) {
  const { locale } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<FacilitySubcategoryDto | null>(null);
  const [deletingSub, setDeletingSub] = useState<FacilitySubcategoryDto | null>(null);

  const [subName, setSubName] = useState('');
  const [subNameKinyarwanda, setSubNameKinyarwanda] = useState('');
  const [subNameFrench, setSubNameFrench] = useState('');
  const [subCode, setSubCode] = useState('');

  const createSub = useCreateSubcategory(facilityType.id);
  const updateSub = useUpdateSubcategory(facilityType.id);
  const deleteSub = useDeleteSubcategory(facilityType.id);

  const subcategories = facilityType.subcategories ?? [];

  const resetForm = () => {
    setSubName('');
    setSubNameKinyarwanda('');
    setSubNameFrench('');
    setSubCode('');
  };

  const openEdit = (sub: FacilitySubcategoryDto) => {
    setSubName(sub.name);
    setSubNameKinyarwanda(sub.nameKinyarwanda ?? '');
    setSubNameFrench(sub.nameFrench ?? '');
    setSubCode(sub.code ?? '');
    setEditingSub(sub);
  };

  return (
    <div className='mt-3 border-t border-gray-100 pt-3'>
      <button
        type='button'
        className='flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900'
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown className='h-3.5 w-3.5' /> : <ChevronRight className='h-3.5 w-3.5' />}
        Subcategories ({subcategories.length})
      </button>

      {expanded && (
        <div className='mt-2 space-y-1.5'>
          {subcategories.length === 0 ? (
            <p className='text-xs text-gray-400 italic'>No subcategories yet.</p>
          ) : (
            subcategories.map(sub => (
              <div key={sub.id} className='flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5'>
                <span className='flex-1 font-medium text-gray-800'>{getLocalizedSubcategoryName(sub, locale)}</span>
                {sub.code && <span className='text-gray-400 font-mono'>{sub.code}</span>}
                {canUpdate && (
                  <button
                    type='button'
                    onClick={() => openEdit(sub)}
                    className='text-primary hover:text-primary'
                    title='Edit subcategory'
                  >
                    <Pencil className='h-3 w-3' />
                  </button>
                )}
                {canDelete && (
                  <button
                    type='button'
                    onClick={() => setDeletingSub(sub)}
                    className='text-red-600 hover:text-red-800'
                    title='Delete subcategory'
                  >
                    <Trash2 className='h-3 w-3' />
                  </button>
                )}
              </div>
            ))
          )}
          {canCreate && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='text-xs h-7 mt-1'
              onClick={() => { resetForm(); setAddOpen(true); }}
            >
              + Add Subcategory
            </Button>
          )}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={open => { if (!open) setAddOpen(false); }}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Add Subcategory</DialogTitle>
            <DialogDescription>Add a subcategory to <strong>{facilityType.name}</strong>.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async e => {
              e.preventDefault();
              if (!subName.trim()) { toast.error('Name is required'); return; }
              await createSub.mutateAsync({
                name: subName.trim(),
                nameKinyarwanda: subNameKinyarwanda.trim() || undefined,
                nameFrench: subNameFrench.trim() || undefined,
                code: subCode.trim() || undefined,
              });
              setAddOpen(false);
              resetForm();
            }}
            className='space-y-3'
          >
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (English) *</label>
              <Input value={subName} onChange={e => setSubName(e.target.value)} placeholder='e.g. Primary School' />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (Kinyarwanda)</label>
              <Input value={subNameKinyarwanda} onChange={e => setSubNameKinyarwanda(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (French)</label>
              <Input value={subNameFrench} onChange={e => setSubNameFrench(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Code</label>
              <Input value={subCode} onChange={e => setSubCode(e.target.value.toUpperCase().replace(/\s/g, '_'))} placeholder='PRI_SCH' />
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setAddOpen(false)} disabled={createSub.isPending}>Cancel</Button>
              <Button type='submit' disabled={createSub.isPending}>
                {createSub.isPending ? <><Loader2 className='h-4 w-4 animate-spin mr-2 inline' />Creating…</> : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingSub} onOpenChange={open => { if (!open) setEditingSub(null); }}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async e => {
              e.preventDefault();
              if (!editingSub) return;
              if (!subName.trim()) { toast.error('Name is required'); return; }
              await updateSub.mutateAsync({
                id: editingSub.id,
                body: {
                  name: subName.trim(),
                  nameKinyarwanda: subNameKinyarwanda.trim() || undefined,
                  nameFrench: subNameFrench.trim() || undefined,
                  code: subCode.trim() || undefined,
                },
              });
              setEditingSub(null);
              resetForm();
            }}
            className='space-y-3'
          >
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (English) *</label>
              <Input value={subName} onChange={e => setSubName(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (Kinyarwanda)</label>
              <Input value={subNameKinyarwanda} onChange={e => setSubNameKinyarwanda(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (French)</label>
              <Input value={subNameFrench} onChange={e => setSubNameFrench(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Code</label>
              <Input value={subCode} onChange={e => setSubCode(e.target.value.toUpperCase().replace(/\s/g, '_'))} />
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setEditingSub(null)} disabled={updateSub.isPending}>Cancel</Button>
              <Button type='submit' disabled={updateSub.isPending}>
                {updateSub.isPending ? <><Loader2 className='h-4 w-4 animate-spin mr-2 inline' />Saving…</> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deletingSub}
        onOpenChange={open => !open && setDeletingSub(null)}
        onConfirm={async () => {
          if (!deletingSub) return;
          await deleteSub.mutateAsync(deletingSub.id);
          setDeletingSub(null);
        }}
        title='Delete subcategory'
        description={deletingSub ? `Are you sure you want to delete "${getLocalizedSubcategoryName(deletingSub, locale)}"?` : ''}
        confirmText='Delete'
        variant='danger'
        isLoading={deleteSub.isPending}
      />
    </div>
  );
}

export default function FacilityTypesManagement() {
  const { dictionary, locale } = useLocale();
  const facilityTypesQuery = useFacilityTypes();
  const existingTypes = facilityTypesQuery.data ?? [];
  const isLoading = facilityTypesQuery.isLoading;
  const createMutation = useCreateFacilityType();
  const updateMutation = useUpdateFacilityType();
  const deleteMutation = useDeleteFacilityType();
  const currentUser = getCurrentUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [editType, setEditType] = useState<FacilityTypeDto | null>(null);
  const [deleteType, setDeleteType] = useState<FacilityTypeDto | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editNameKinyarwanda, setEditNameKinyarwanda] = useState('');
  const [editNameFrench, setEditNameFrench] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editFacilityType, setEditFacilityType] = useState<'PRIVATE' | 'PUBLIC_PLACE'>('PRIVATE');

  const openEdit = (ft: FacilityTypeDto) => {
    setEditName(ft.name);
    setEditNameKinyarwanda(ft.nameKinyarwanda ?? '');
    setEditNameFrench(ft.nameFrench ?? '');
    setEditCode(ft.code ?? '');
    setEditFacilityType(ft.type as 'PRIVATE' | 'PUBLIC_PLACE' ?? 'PRIVATE');
    setEditType(ft);
  };

  const [name, setName] = useState('');
  const [nameKinyarwanda, setNameKinyarwanda] = useState('');
  const [nameFrench, setNameFrench] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState<'PRIVATE' | 'PUBLIC_PLACE'>('PRIVATE');

  const resetForm = () => {
    setName('');
    setNameKinyarwanda('');
    setNameFrench('');
    setCode('');
    setType('PRIVATE');
  };

  // Permission-based access control. Navbar already hides the route, but this
  // ensures direct navigation still respects the role permissions.
  const canRead = hasPermission(currentUser as any, 'FACILITY_TYPES_READ');
  const canCreate = hasPermission(currentUser as any, 'FACILITY_TYPES_CREATE');
  const canUpdate = hasPermission(currentUser as any, 'FACILITY_TYPES_UPDATE');
  const canDelete = hasPermission(currentUser as any, 'FACILITY_TYPES_DELETE');

  if (!canRead) {
    return (
      <div className='p-6'>
        <div className='bg-red-50 border-l-4 border-red-500 p-6 rounded-lg'>
          <p className='text-red-700'>
            {dictionary['facility_types.access_denied'] || "You don't have permission to manage facility types."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-[1600px] mx-auto'>
      <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3'>
        <div>
          <h1 className='text-lg sm:text-xl lg:text-2xl font-bold text-gray-900'>{dictionary['facility_types.title']}</h1>
          <p className='text-xs sm:text-sm text-gray-600 mt-1'>{dictionary['facility_types.subtitle']}</p>
        </div>

        {canCreate && (
          <Button
            type='button'
            className='gap-2'
            onClick={() => {
              resetForm();
              setCreateOpen(true);
            }}
            disabled={createMutation.isPending}
          >
            {dictionary['facility_types.add_new'] || 'Add New Facility Type'}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='text-base sm:text-lg'>
            {dictionary['facility_types.existing_count'].replace('{count}', existingTypes.length.toString())}
          </CardTitle>
          <CardDescription className='text-xs sm:text-sm'>{dictionary['facility_types.existing_desc']}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8 sm:py-12 text-gray-500 text-sm'>
              {dictionary['facility_types.loading']}
            </div>
          ) : existingTypes.length === 0 ? (
            <div className='text-center py-8 sm:py-12 text-gray-500 text-sm'>
              {dictionary['facility_types.no_types']}
            </div>
          ) : (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
              {existingTypes.map(facilityType => (
                <div
                  key={facilityType.id}
                  className='flex flex-col p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors'
                >
                  <div className='flex items-start gap-3'>
                    <div className='bg-secondary rounded-lg p-2 flex-shrink-0'>
                      <Building2 className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='font-medium text-sm sm:text-base text-gray-900 truncate'>
                        {getLocalizedFacilityTypeName(facilityType, locale)}
                      </p>
                      {facilityType.description && (
                        <p className='text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2'>{facilityType.description}</p>
                      )}
                    </div>
                    <div className='flex gap-1 flex-shrink-0'>
                      {canUpdate && (
                        <Button
                          type='button'
                          size='sm'
                          variant='outline'
                          onClick={() => openEdit(facilityType)}
                          className='text-primary border-primary/30 hover:bg-secondary'
                          title='Edit facility type'
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          type='button'
                          size='sm'
                          variant='outline'
                          onClick={() => setDeleteType(facilityType)}
                          className='text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'
                          disabled={deleteMutation.isPending}
                          title='Delete facility type'
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      )}
                    </div>
                  </div>

                  <SubcategoryManager facilityType={facilityType} canCreate={canCreate} canUpdate={canUpdate} canDelete={canDelete} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={open => {
          if (!open) setCreateOpen(false);
        }}
      >
        <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{dictionary['facility_types.add_new_title'] || 'Add New Facility Type'}</DialogTitle>
            <DialogDescription>
              {dictionary['facility_types.add_new_desc'] ||
                'Create a new facility type. You can specify multiple responsible ministries (comma-separated).'}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={async e => {
              e.preventDefault();
              const payload: CreateFacilityTypeDto = {
                name: name.trim(),
                nameKinyarwanda: nameKinyarwanda.trim(),
                nameFrench: nameFrench.trim(),
                code: code.trim(),
                type,
                responsibleMinistries: ['MINISTRY_OF_EDUCATION'],
              };

              if (!payload.name || !payload.nameKinyarwanda || !payload.nameFrench || !payload.code) {
                toast.error('Please fill all required fields.');
                return;
              }

              await createMutation.mutateAsync(payload);
              setCreateOpen(false);
              resetForm();
            }}
            className='space-y-4'
          >
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (English)</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder='Private School' />
            </div>

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (Kinyarwanda)</label>
              <Input value={nameKinyarwanda} onChange={e => setNameKinyarwanda(e.target.value)} placeholder='Ishuri Ryigenga' />
            </div>

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (French)</label>
              <Input value={nameFrench} onChange={e => setNameFrench(e.target.value)} placeholder='École Privée' />
            </div>

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Code</label>
              <Input value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, '_'))} placeholder='PRI_SCH' />
            </div>

            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value as 'PRIVATE' | 'PUBLIC_PLACE')}
                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              >
                <option value='PRIVATE'>PRIVATE</option>
                <option value='PUBLIC_PLACE'>PUBLIC_PLACE</option>
              </select>
            </div>

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => setCreateOpen(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin mr-2 inline' />
                    Creating…
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Facility Type Dialog */}
      <Dialog open={!!editType} onOpenChange={open => { if (!open) setEditType(null); }}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Edit Facility Type</DialogTitle>
            <DialogDescription>Update the details for this facility type.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async e => {
              e.preventDefault();
              if (!editType) return;
              if (!editName.trim()) { toast.error('Name is required'); return; }
              await updateMutation.mutateAsync({
                id: editType.id,
                data: {
                  name: editName.trim(),
                  nameKinyarwanda: editNameKinyarwanda.trim() || undefined,
                  nameFrench: editNameFrench.trim() || undefined,
                  code: editCode.trim() || undefined,
                  type: editFacilityType,
                },
              });
              setEditType(null);
            }}
            className='space-y-4'
          >
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (English) *</label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (Kinyarwanda)</label>
              <Input value={editNameKinyarwanda} onChange={e => setEditNameKinyarwanda(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Name (French)</label>
              <Input value={editNameFrench} onChange={e => setEditNameFrench(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Code</label>
              <Input value={editCode} onChange={e => setEditCode(e.target.value.toUpperCase().replace(/\s/g, '_'))} />
            </div>
            <div className='space-y-1'>
              <label className='block text-sm font-medium text-gray-700'>Type</label>
              <select
                value={editFacilityType}
                onChange={e => setEditFacilityType(e.target.value as 'PRIVATE' | 'PUBLIC_PLACE')}
                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              >
                <option value='PRIVATE'>PRIVATE</option>
                <option value='PUBLIC_PLACE'>PUBLIC_PLACE</option>
              </select>
            </div>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => setEditType(null)} disabled={updateMutation.isPending}>Cancel</Button>
              <Button type='submit' disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <><Loader2 className='h-4 w-4 animate-spin mr-2 inline' />Saving…</> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteType}
        onOpenChange={open => !open && setDeleteType(null)}
        onConfirm={async () => {
          if (!deleteType) return;
          await deleteMutation.mutateAsync(deleteType.id);
          setDeleteType(null);
        }}
        title='Delete facility type'
        description={
          deleteType
            ? `Are you sure you want to delete "${getLocalizedFacilityTypeName(deleteType, locale)}"?`
            : ''
        }
        confirmText='Delete'
        variant='danger'
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
